import type { HttpTransport } from '@mangovoice/api-client'
import type { PaginatedApiResult } from '@mangovoice/types'
import type { components } from '@mangovoice/types/generated/mango-api'

import { stringifyParams } from './utils'

type DeviceRead = components['schemas']['DeviceRead']
type DeviceModelSchema = components['schemas']['DeviceModel']

export type DeviceListItem = DeviceRead

export type DeviceDetail = DeviceRead

export type DeviceModel = DeviceModelSchema

export interface DevicesListParams {
  pbx_id: number
  limit?: number
  offset?: number
  search?: string
  ordering?: string
  manufacturer_id?: number
  device_model?: number
  firmware_version?: string
  warranty?: string
  transport?: string
  jitter?: string
  sidecar_enabled?: boolean
  provisioning?: string
}

export function createDevicesResource(transport: HttpTransport) {
  return {
    getDevices: (params: DevicesListParams) => {
      const { pbx_id, ...queryParams } = params
      const qs = stringifyParams(queryParams as Record<string, unknown>)
      return transport.get<PaginatedApiResult<DeviceListItem>>(
        qs
          ? `/app/v1/pbxes/${pbx_id}/devices/?${qs}`
          : `/app/v1/pbxes/${pbx_id}/devices/`,
      )
    },

    getDevice: (pbxId: number, deviceId: number) =>
      transport.get<DeviceDetail>(
        `/app/v1/pbxes/${pbxId}/devices/${deviceId}/`,
      ),

    createDevice: (pbxId: number, payload: Record<string, unknown>) =>
      transport.post<DeviceDetail>(`/app/v1/pbxes/${pbxId}/devices/`, payload),

    updateDevice: (
      pbxId: number,
      deviceId: number,
      payload: Record<string, unknown>,
    ) =>
      transport.patch<DeviceDetail>(
        `/app/v1/pbxes/${pbxId}/devices/${deviceId}/`,
        payload,
      ),

    deleteDevice: (pbxId: number, deviceId: number) =>
      transport.delete<void>(`/app/v1/pbxes/${pbxId}/devices/${deviceId}/`),

    rebuildDevices: (pbxId: number) =>
      transport.post<{ success?: boolean }>(
        `/app/v1/pbxes/${pbxId}/devices/rebuild/`,
      ),

    rebuildAndRebootDevices: (pbxId: number) =>
      transport.post<{ success?: boolean }>(
        `/app/v1/pbxes/${pbxId}/devices/rebuild-and-reboot/`,
      ),

    reloadDeviceConfig: (pbxId: number, deviceIds: number[]) =>
      transport.post<unknown>(
        `/app/v1/pbxes/${pbxId}/devices/bulk-reload-config/`,
        { ids: deviceIds },
      ),

    rebootDevice: (pbxId: number, deviceIds: number[]) =>
      transport.post<unknown>(
        `/app/v1/pbxes/${pbxId}/devices/bulk-reload-config-and-reboot/`,
        { ids: deviceIds },
      ),

    massEditDevices: (pbxId: number, payload: Record<string, unknown>[]) =>
      transport.patch<DeviceListItem[]>(
        `/app/v1/pbxes/${pbxId}/devices/mass-edit/`,
        payload,
      ),

    validateMac: (pbxId: number, macAddress: string, deviceId?: number) => {
      const qs = stringifyParams({
        mac_address: macAddress,
        device_id: deviceId,
      })
      return transport.get<{ valid?: boolean }>(
        `/app/v1/pbxes/${pbxId}/devices/validate-mac-address/?${qs}`,
      )
    },

    getModels: () => transport.get<DeviceModel[]>('/app/v1/device-models/'),

    getDirectories: (pbxId: number) =>
      transport.get<unknown>(`/app/v1/pbxes/${pbxId}/directories/`),

    getSystemDefaults: (pbxId: number) =>
      transport.get<unknown>(`/app/v1/pbx/${pbxId}/system-settings/`),

    getExtensionLines: (pbxId: number) =>
      transport.get<unknown>(`/app/v1/pbxes/${pbxId}/extension-lines/`),

    getSharedVoicemailBoxes: (pbxId: number) =>
      transport.get<unknown>(`/app/v1/pbxes/${pbxId}/shared-voicemail-box/`),

    getUsers: (pbxId: number) =>
      transport.get<unknown>(`/app/v2/users/?pbx_id=${pbxId}&paginate=false`),
  }
}
